import { type CSSProperties } from 'react'
import { useTranslation } from '../i18n'
import { COLORS } from '../lib/theme'
import { speciesDisplayName, type HuntableGameSpecies } from '../lib/huntable-game-catalog'
import { huntingYearWindow, lastNDaysWindow } from '../lib/huntingYear'

/** One species' downed/observed tally over the selected period. */
export interface TeamGameReportSpeciesStat {
  /** Species wire id (a game pin's `games[].name`); resolved to a localized
      display name via the game-name catalog. */
  species: string
  downed: number
  observed: number
  total: number
}

/** The team game-report statistics payload, aggregated over an inclusive date
    range. Structurally the wire contract the .NET API returns. */
export interface TeamGameReportStats {
  from: string
  to: string
  totalAnimals: number
  byType: { downed: number; observed: number }
  bySpecies: TeamGameReportSpeciesStat[]
}

/** The selected reporting period, as ISO-8601 instants. */
export interface StatisticsPeriod {
  from: string
  to: string
}

export interface TeamStatisticsSectionProps {
  /** The stats for the current period. Absent while the first load is pending. */
  data?: TeamGameReportStats
  /** True while a fetch is in flight and no data is shown yet. */
  loading?: boolean
  period: StatisticsPeriod
  onPeriodChange: (period: StatisticsPeriod) => void
  /** The live game-name catalog, used to localize species labels. Omitted → the
      raw wire ids are shown. Presentation-only: the container supplies it. */
  speciesCatalog?: readonly HuntableGameSpecies[]
  /** Clock injection point for deterministic tests; defaults to the real now. */
  now?: Date
}

const toWindow = (from: Date, to: Date): StatisticsPeriod => ({
  from: from.toISOString(),
  to: to.toISOString(),
})

/** yyyy-mm-dd for an `<input type="date">`, from an ISO instant. */
function dateInputValue(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function TeamStatisticsSection({
  data,
  loading,
  period,
  onPeriodChange,
  speciesCatalog = [],
  now = new Date(),
}: TeamStatisticsSectionProps) {
  const { t } = useTranslation('statistics')

  const hy = huntingYearWindow(now)
  const l30 = lastNDaysWindow(now)
  const huntingYearPeriod = toWindow(hy.from, hy.to)
  const last30Period = toWindow(l30.from, l30.to)

  const isHuntingYear = period.from === huntingYearPeriod.from && period.to === huntingYearPeriod.to
  const isLast30 = period.from === last30Period.from && period.to === last30Period.to
  const isCustom = !isHuntingYear && !isLast30

  const setCustomFrom = (value: string) => {
    if (!value) return
    const [y, m, d] = value.split('-').map(Number)
    onPeriodChange({ ...period, from: new Date(y, m - 1, d, 0, 0, 0, 0).toISOString() })
  }
  const setCustomTo = (value: string) => {
    if (!value) return
    const [y, m, d] = value.split('-').map(Number)
    onPeriodChange({ ...period, to: new Date(y, m - 1, d, 23, 59, 59, 999).toISOString() })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.onSurfaceVariant }}>
          {t('period.label')}
        </span>
        <PresetButton
          testId="preset-huntingYear"
          active={isHuntingYear}
          label={t('period.thisHuntingYear')}
          onClick={() => onPeriodChange(huntingYearPeriod)}
        />
        <PresetButton
          testId="preset-last30"
          active={isLast30}
          label={t('period.last30Days')}
          onClick={() => onPeriodChange(last30Period)}
        />
        <PresetButton
          testId="preset-custom"
          active={isCustom}
          label={t('period.custom')}
          // Seed a custom range from the current window so the pickers open on
          // meaningful dates rather than an empty range.
          onClick={() => onPeriodChange({ ...period })}
        />
        {isCustom && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 12, color: COLORS.onSurfaceVariant }}>
              {t('period.from')}{' '}
              <input
                type="date"
                aria-label={t('period.from')}
                value={dateInputValue(period.from)}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
            </label>
            <label style={{ fontSize: 12, color: COLORS.onSurfaceVariant }}>
              {t('period.to')}{' '}
              <input
                type="date"
                aria-label={t('period.to')}
                value={dateInputValue(period.to)}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </label>
          </span>
        )}
      </div>

      {loading ? (
        <div data-testid="statistics-loading" style={mutedBoxStyle}>
          {t('loading')}
        </div>
      ) : !data || data.totalAnimals === 0 ? (
        <div data-testid="statistics-empty" style={mutedBoxStyle}>
          {t('empty')}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <StatCard testId="stat-downed" label={t('totals.downed')} value={data.byType.downed} />
            <StatCard testId="stat-observed" label={t('totals.observed')} value={data.byType.observed} />
            <StatCard testId="stat-total" label={t('totals.total')} value={data.totalAnimals} accent />
          </div>

          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: COLORS.onSurface, margin: '4px 0 8px' }}>
              {t('species.heading')}
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr>
                  <th style={thStyle('left')}>{t('species.columnSpecies')}</th>
                  <th style={thStyle('right')}>{t('species.columnDowned')}</th>
                  <th style={thStyle('right')}>{t('species.columnObserved')}</th>
                  <th style={thStyle('right')}>{t('species.columnTotal')}</th>
                </tr>
              </thead>
              <tbody>
                {data.bySpecies.map((row) => (
                  <tr key={row.species} data-testid={`species-row-${row.species}`}>
                    <td style={tdStyle('left')}>{speciesDisplayName(speciesCatalog, row.species)}</td>
                    <td style={tdStyle('right')} data-testid="species-downed">{row.downed}</td>
                    <td style={tdStyle('right')} data-testid="species-observed">{row.observed}</td>
                    <td style={{ ...tdStyle('right'), fontWeight: 700 }} data-testid="species-total">{row.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

const mutedBoxStyle: CSSProperties = {
  padding: '24px 16px',
  textAlign: 'center',
  color: COLORS.onSurfaceVariant,
  background: COLORS.surfaceContainerLow,
  borderRadius: 10,
  fontSize: 14,
}

function PresetButton({
  active,
  label,
  onClick,
  testId,
}: {
  active: boolean
  label: string
  onClick: () => void
  testId: string
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      style={{
        padding: '6px 12px',
        fontSize: 13,
        fontWeight: 600,
        borderRadius: 999,
        cursor: 'pointer',
        border: `1px solid ${active ? COLORS.primary : COLORS.outline}`,
        background: active ? COLORS.primary : 'transparent',
        color: active ? COLORS.onPrimary : COLORS.onSurface,
      }}
    >
      {label}
    </button>
  )
}

function StatCard({
  label,
  value,
  accent,
  testId,
}: {
  label: string
  value: number
  accent?: boolean
  testId: string
}) {
  return (
    <div
      data-testid={testId}
      style={{
        flex: '1 1 120px',
        minWidth: 120,
        padding: '14px 16px',
        borderRadius: 12,
        background: accent ? COLORS.primaryContainer : COLORS.surfaceContainerLow,
        border: `1px solid ${COLORS.outline}`,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.onSurfaceVariant }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: accent ? COLORS.onPrimaryContainer : COLORS.onSurface }}>
        {value}
      </div>
    </div>
  )
}

const thStyle = (align: 'left' | 'right'): CSSProperties => ({
  textAlign: align,
  padding: '8px 10px',
  fontSize: 12,
  fontWeight: 600,
  color: COLORS.onSurfaceVariant,
  borderBottom: `1px solid ${COLORS.outline}`,
})

const tdStyle = (align: 'left' | 'right'): CSSProperties => ({
  textAlign: align,
  padding: '10px',
  color: COLORS.onSurface,
  borderBottom: `1px solid ${COLORS.surfaceContainerHigh}`,
})
